import { Cards } from "@/types/dto/homepage/homepage-types";
import sanitizeHtml from "@/utils/sanitize-html";
import { processImageUrl } from "@/utils/widen-image-utils";
import { Box, Typography, alpha } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import React from "react";


export interface CampaignCardProps {
  card: Cards;
  count?: number;
  cardIndex?: number;
}

// eslint-disable-next-line react/prop-types
const CampaignCards: React.FC<CampaignCardProps> = ({ card, count, cardIndex }) => {
  // Convert Widen images to WebP for better performance
  const imageUrl = processImageUrl(card.image.imageUrl || "");

  // Default maxWidth for carousel mode (used on mobile and for 4+ cards)
  let maxWidth: string | object = { xs: "350px", sm: "466px", md: "610px", lg: "700px" };
  let marginXValue: string | number | object = "1rem";
  // For 2 or 3 cards on large desktop (>1200px), we use flex: 1 so cards share space equally
  const isFlexDesktop = count === 2 || count === 3;

  if (count === 1) {
    maxWidth = "100%";
    marginXValue = 0;
  } else if (count === 2) {
    // Below 1200px: carousel mode, cards need constrained width
    // Above 1200px: flex layout, cards share space (no maxWidth limit)
    maxWidth = { xs: "300px", sm: "400px", md: "500px", lg: "none", xl: "none" };
    marginXValue = { xs: "1rem", lg: 0 }; // No margin on large desktop, gap handles spacing
  } else if (count === 3) {
    // Below 1200px: carousel mode, cards need constrained width
    // Above 1200px: flex layout, cards share space (no maxWidth limit)
    maxWidth = { xs: "350px", sm: "466px", md: "500px", lg: "none", xl: "none" };
    marginXValue = { xs: "1rem", lg: 0 }; // No margin on large desktop, container handles spacing
  }

  return (
    <Box
      sx={{
        borderRadius: "2rem",
        padding: "4rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        alignItems: "center",
        justifyContent: "flex-end",
        position: "relative",
        boxShadow: 6,
        boxSizing: "border-box",
        color: "common.white",
        overflow: "hidden", // Important to clip  the scaled image
        marginX: marginXValue, // Dynamic margin based on card count
        maxWidth,
        ...(isFlexDesktop && { flex: { xs: "none", md: 1 } }),
        height: {xs:"385px", sm: "404px"},
        cursor: "pointer",
        "&:hover .background-image": {
          transform: "scale(1.05)"
        }
      }}
      data-testid={`campaign-card-${card.id}`}
      data-card-name={card.info?.headerText || card.title}
      data-carousel-position={cardIndex !== undefined ? String(cardIndex + 1) : undefined}
    >
      {/* Background Image - Next.js Image with unoptimized to respect Widen parameters */}
      <Box className="background-image"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "2rem",
            overflow: "hidden",
            transition: "transform 0.3s ease-in-out",
            zIndex: 1
          }}
      >
        <Image
          src={imageUrl || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3C/svg%3E"}
          alt={card.image.altText || card.info?.headerText || "Campaign image"}
          fill
          unoptimized
          style={{
            objectFit: "cover",
            objectPosition: "center"
          }}
          sizes="(max-width: 768px) 350px, (max-width: 1024px) 610px, 700px"
        />
      </Box>
      {/* Gradient overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "2rem",
          background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0)} 0%, ${alpha(theme.palette.common.black, 0.8)} 100%)`,
          zIndex: 1,
          pointerEvents: "none"
        }}
      />
      <Box
        sx={{
          zIndex: 2,
          position: "relative",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: { xs: "1rem", md: "2rem" }
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontFamily: "Montserrat",
            fontSize: "32px",
            lineHeight: "100%",
            fontWeight: 600,
            alignSelf: "stretch",
            textTransform: "uppercase"
          }}
          data-testid={`campaign-card-header-${card.id}`}
        >
          {card.info?.headerText}
        </Typography>

        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontSize: "18px",
            fontStyle: "normal",
            lineHeight: "150%",
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            alignSelf: "stretch",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: { xs: 4, sm: "unset" }
          }}
          component="p"
          variant="subtitle_lg"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.info?.copyText?.replace(/<p>/g, "").replace(/<\/p>/g, "")) }}
        />

        <Typography
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: "16px",
            fontStyle: "normal",
            lineHeight: "150%",
            letterSpacing: "0.025rem",
            fontWeight: 700,
            color: "info.dark",
            textDecoration: "underline",
            textDecorationStyle: "solid",
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            position: "relative",
            zIndex: 10
          }}
        >
          {card.info?.callToActionText || "Learn More"}
        </Typography>
      </Box>
      {card.badge && (
        <Box
          sx={{
            backgroundColor: "background.cool_strong",
            borderRadius: "10px",
            padding: "6px",
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            position: "absolute",
            right: "32px",
            top: "32px",
            zIndex: 2
          }}
        >
          <Typography
            sx={{
              color: "text.primary",
              textAlign: "right",
              fontFamily: "Montserrat",
              fontSize: "1.2rem",
              lineHeight: "150%",
              letterSpacing: "0.4px",
              fontWeight: 600,
              position: "relative"
            }}
          >
            {card.badge}
          </Typography>
        </Box>
      )}
      
      {/* Disclaimer text at right bottom */}
      <Typography
        sx={{
          position: "absolute",
          right: "2rem",
          bottom: "2rem",
          zIndex: 2,
          fontFamily: "Montserrat",
          fontSize: "11px",
          lineHeight: "120%",
          fontWeight: 400,
          color: (theme) => alpha(theme.palette.common.white, 0.8),
          textAlign: "right"
        }}
      >
        {card.subscriptText}
      </Typography>
      
      {/* Full card clickable link using pseudo-absolute-full-container */}
      <Link
        href={card.info?.callToActionUrl || "#"}
        rel="noopener noreferrer"
        tabIndex={-1}
        style={{ zIndex: 5 }}
        className="pseudo-absolute-full-container"
        data-testid={`campaign-card-link-${card.id}`}
        aria-label={`${card.info?.headerText || "Campaign"} - ${card.info?.callToActionText || "Learn More"}`}
      />
    </Box>
  );
};

export default CampaignCards;